import { ExclamationCircleFilled } from '@ant-design/icons';
import { Divider, message } from 'antd';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAndroid, isAppleTV, isFlutter, isHarmonyOS, isIOS, isMac, isWindows } from '@/infrastructure/product-info';
import { Platform } from '@/infrastructure/utils/app';
import {
  ConfigTabType, deleteConfig, FormatConfig,
  FullConfig, isConfigManage, loadConfigDetail,
} from '@/presentation/pages/setting/app-deliver-config/api';
import { ConfirmModal } from '@/presentation/pages/setting/app-deliver-config/config-modal/confirm';
import { EditConfigModal, OperationType } from '@/presentation/pages/setting/app-deliver-config/config-modal/edit';
import { ViewConfigModal } from '@/presentation/pages/setting/app-deliver-config/config-modal/view';
import style from './index.module.scss';

interface IProps {
  tab: ConfigTabType;
  data: FullConfig;
  update: () => void;
  defaultConfig: string;
  loadDefaultConfig: (platform?: Platform) => void;
}

export const ConfigBtn: FC<IProps> = (props) => {
  const { t } = useTranslation();
  const { tab, data, update, defaultConfig, loadDefaultConfig } = props;
  const { id, name, auth, platform } = data;
  const [fieldsValue, setFieldsValue] = useState<FormatConfig>({
    id: 0,
    productId: '',
    name: '',
    createUser: '',
    createTime: 0,
    updateUser: '',
    updateTime: 0,
    enabled: 0,
    priority: 0,
  });
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [operation, setOperation] = useState<OperationType>(OperationType.UPDATE);

  const onViewClick = async () => {
    const messageHide = message.loading({ content: t('加载中...'), key: 'showBtn' });
    const data = await loadConfigDetail(tab, id);
    messageHide();
    if (!data) {
      return;
    }
    setFieldsValue(data);
    setViewModalVisible(true);
  };

  const onEditClick = async (operation: OperationType) => {
    if (!auth && !isConfigManage(tab)) {
      return;
    }
    if (isConfigManage(tab)) {
      loadDefaultConfig?.(platform);
    }
    setOperation(operation);
    const messageHide = message.loading({ content: t('加载中...'), key: 'updateBtn' });
    const data = await loadConfigDetail(tab, id);
    messageHide();
    if (!data) {
      return;
    }
    setFieldsValue({
      ...data,
      name: operation === OperationType.COPY ? t('{{name}}-副本', { name: data?.name }) : data?.name,
    });
    setEditModalVisible(true);
  };

  const onEditFinish = () => {
    setEditModalVisible(false);
    update();
  };

  const onDeleteClick = () => {
    if (!auth && !isConfigManage(tab)) {
      return;
    }
    setDeleteModalVisible(true);
  };

  const onDeleteConfirm = async () => {
    setDeleteModalVisible(false);
    const messageHide = message.loading({ content: t('删除中...'), key: 'onDelOk' });
    const resp = await deleteConfig(tab, id);
    messageHide();
    if (resp?.baseRsp?.code === 0) {
      update();
      message.success({ content: t('删除成功'), key: 'onDelOk', duration: 2 });
    }
  };

  const disable = (name: string) => name === t('V5配置') || name === t('V6配置');

  return (
    <div className={style.configBtn}>
      <div className={style.btnText} onClick={onViewClick}>{t('查看')}</div>
      {(isAndroid() || isMac() || isIOS()
        || isAppleTV() || isConfigManage(tab) || isFlutter() || isWindows() || isHarmonyOS()) ? <>
          <Divider type={'vertical'}/>
          <div
            className={disable(name) || (!auth && !isConfigManage(tab)) ? style.btnTextV5 : style.btnText}
            onClick={() => (disable(name) ? null : onEditClick(OperationType.UPDATE))}
          >{t('编辑')}</div>
          <Divider type={'vertical'}/>
          {(isConfigManage(tab) || tab === ConfigTabType.BUSINESS) && <>
            <div
              className={disable(name) || (!auth && !isConfigManage(tab)) ? style.btnTextV5 : style.btnText}
              onClick={() => (disable(name) ? null : onEditClick(OperationType.COPY))}
            >{t('复制')}</div>
            <Divider type={'vertical'}/>
          </>}
          <div
            className={!auth && !isConfigManage(tab) ? style.btnTextV5 : style.btnText }
            onClick={onDeleteClick}
          >{t('删除')}</div>
        </> : null}
      <ViewConfigModal
        visible={viewModalVisible}
        tab={tab}
        onOk={() => setViewModalVisible(false)}
        onCancel={() => setViewModalVisible(false)}
        values={fieldsValue}
      />
      <EditConfigModal
        visible={editModalVisible}
        type={operation}
        tab={tab}
        values={fieldsValue}
        onChange={onEditFinish}
        onCancel={() => setEditModalVisible(false)}
        defaultConfig={defaultConfig}
      />
      <ConfirmModal
        visible={deleteModalVisible}
        icon={<ExclamationCircleFilled style={{ fontSize: '21px', color: '#F28B24' }} />}
        onOk={onDeleteConfirm}
        onCancel={() => setDeleteModalVisible(false)}
        confirmTitle={t('确认删除所选配置？')}
        confirmPrompt={t('删除后，该配置任务不再生效。')}
      />
    </div>
  );
};
